import React, { useEffect, useMemo, useState } from "react";
import {
    Loader2,
    AlertCircle,
    BarChart3,
    RefreshCw,
    Users,
    CheckCircle,
    Activity,
    TrendingUp,
    Clock,
} from "lucide-react";
import api from "../../api"; // Use centralized API client

export default function SessionAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const fetchAnalytics = async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            const res = await api.get("api/chatbot/analytics", {
                timeout: 30000,
            });

            // Normalize
            let payload = res.data;
            if (payload?.data) payload = payload.data;
            setData(payload || {});
        } catch (e) {
            console.error("Error fetching analytics:", e);
            const detail =
                e.response?.data?.detail ||
                e.response?.data?.message ||
                "Failed to fetch analytics.";
            setErrorMsg(detail);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const StatCard = ({ icon: Icon, label, value, sub }) => (
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: "#55c5d0" }}>
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <div className="text-sm text-gray-500">{label}</div>
                    <div className="text-2xl font-semibold text-slate-900">{value ?? "—"}</div>
                    {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
                </div>
            </div>
        </div>
    );

    const flow = data?.flow_breakdown || {};
    const recent7 = data?.recent_activity?.sessions_last_7_days;

    return (
        <section className="space-y-6 p-6 font-inter bg-slate-50 min-h-screen">
            {/* Header actions */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: "#55c5d0" }}>
                        <BarChart3 className="w-5 h-5" />
                    </div>
                    <h1 className="text-xl font-semibold text-slate-900">Session Analytics</h1>
                </div>

                <button
                    onClick={fetchAnalytics}
                    disabled={loading}
                    className={`px-5 py-2.5 rounded-xl text-white flex items-center gap-2 font-medium tracking-wide transition-all ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-[#55c5d0] hover:bg-[#48b7c3]"
                        }`}
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {loading ? "Refreshing…" : "Refresh"}
                </button>
            </div>

            {/* Error */}
            {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    <span>{errorMsg}</span>
                </div>
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total Sessions" value={data?.total_sessions} />
                <StatCard icon={CheckCircle} label="Completed Sessions" value={data?.completed_sessions} />
                <StatCard icon={Activity} label="Active Sessions" value={data?.active_sessions} />
                <StatCard
                    icon={TrendingUp}
                    label="Conversion Rate"
                    value={data?.conversion_rate != null ? `${Number(data.conversion_rate).toFixed(2)}%` : "—"}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Leads + Recent activity */}
                <div className="space-y-6 lg:col-span-1">
                    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
                        <div className="flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-slate-900">Leads</h2>
                        </div>
                        <div className="mt-3 text-3xl font-semibold text-slate-900">
                            {data?.leads_created ?? "—"}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Created from sessions</div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
                        <div className="flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-slate-900">Recent Activity</h2>
                            <Clock className="w-4 h-4 text-gray-400" />
                        </div>
                        <div className="mt-3 text-3xl font-semibold text-slate-900">
                            {recent7 ?? "—"}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Sessions in last 7 days</div>
                    </div>
                </div>

                {/* Flow breakdown */}
                <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 lg:col-span-2">
                    <div
                        className="px-4 py-2 -mx-5 -mt-5 text-white text-xs font-semibold tracking-wide uppercase rounded-t-2xl"
                        style={{ backgroundColor: "#55c5d0" }}
                    >
                        Flow Breakdown
                    </div>

                    {flow && Object.keys(flow).length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                            {Object.entries(flow).map(([key, val]) => (
                                <div key={key} className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{key}</div>
                                    <div className="text-2xl font-semibold text-slate-900">{val ?? 0}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm text-gray-600 mt-4">No breakdown available.</div>
                    )}
                </div>
            </div>
        </section>
    );
}
